import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { Flip } from 'gsap/Flip';

/**
 * GSAP setup and the one hook every animated section uses.
 *
 * Two rules run through all of this, and breaking either one breaks the site
 * rather than just the animation:
 *
 * 1. Nothing starts hidden in CSS. Every "fade up" sets its own starting state
 *    from JavaScript, inside the branch that only runs when the visitor has not
 *    asked for reduced motion. If this file fails to load, or someone has
 *    reduced motion switched on, the page is simply a normal page — all the
 *    text is there and all the photos are visible. A hero that is invisible
 *    until an animation runs is a hero that is invisible when it does not.
 *
 * 2. Everything is scoped and reverted. gsap.context() collects the tweens and
 *    ScrollTriggers a section creates, and React reverts them when the section
 *    unmounts. Client-side routing means sections mount and unmount constantly,
 *    and orphaned ScrollTriggers left behind on a route change are what make a
 *    single-page site slowly turn to treacle.
 */

gsap.registerPlugin(ScrollTrigger, SplitText, Flip);

export { gsap, ScrollTrigger, SplitText, Flip };

/**
 * The breakpoints animations are written against.
 *
 * `motion` is the important one: it is false when the visitor has asked their
 * system for reduced motion, and every pin, parallax, and scrub is wrapped in
 * it. gsap.matchMedia re-runs the setup when the preference changes, so turning
 * it on mid-visit tears the animations down and puts the page back.
 */
export const QUERIES = {
	motion: '(prefers-reduced-motion: no-preference)',
	reduced: '(prefers-reduced-motion: reduce)',
	desktop: '(min-width: 62rem) and (prefers-reduced-motion: no-preference)',
	mobile: '(max-width: 61.999rem) and (prefers-reduced-motion: no-preference)',
};

/**
 * Runs a GSAP setup function scoped to an element, and cleans it up.
 *
 * @param {(api: {mm: gsap.MatchMedia, root: HTMLElement}) => void} build
 *        Creates the animations. Receives a matchMedia instance to hang
 *        conditional setups off, and the scope element.
 * @param {unknown[]} [deps] Re-runs the setup when these change, in the same way
 *        useEffect does. Content arrives from the REST API after the first
 *        render, so most sections pass what they are animating.
 * @returns {import('react').RefObject<HTMLElement>} Attach to the section root.
 */
export function useGsap(build, deps = []) {
	const root = useRef(null);

	useLayoutEffect(() => {
		if (!root.current) {
			return undefined;
		}

		const context = gsap.context(() => {
			const mm = gsap.matchMedia();
			build({ mm, root: root.current });
		}, root);

		return () => context.revert();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);

	return root;
}

/**
 * A scroll-triggered fade-up that cleans up after itself.
 *
 * Use this rather than a bare gsap.from() for anything revealed on scroll. A
 * from() tween renders its starting state the moment it is created, so the
 * elements sit displaced from page load and only return to their real position
 * once the trigger fires. If anything interrupts that — a revert, a refresh,
 * React remounting the section — they stay displaced, and because a transform
 * moves what you see but not what the layout measures, they end up painted on
 * top of whatever follows them. That is exactly how the home page's photo grid
 * came to overlap the button beneath it.
 *
 * The three flags below are what prevent it:
 *
 *   immediateRender: false — nothing is displaced before the trigger fires.
 *   clearProps: 'transform' — nothing is left displaced after it finishes.
 *   once: true — it does not re-arm and re-apply the start state on scroll back.
 *
 * @param {gsap.TweenTarget} targets  Elements to reveal.
 * @param {object} options            trigger (required), start, and any tween
 *                                    vars to override — stagger, y, duration.
 * @returns {gsap.core.Tween}
 */
export function reveal(targets, { trigger, start = 'top 80%', ...vars } = {}) {
	return gsap.from(targets, {
		autoAlpha: 0,
		y: 30,
		duration: 0.6,
		ease: 'power2.out',
		...vars,
		immediateRender: false,
		clearProps: 'transform',
		scrollTrigger: { trigger, start, once: true },
	});
}

/**
 * Splits a heading into lines and characters for a per-character reveal.
 *
 * SplitText rewrites the element's innards into a stack of spans, which is
 * fine to look at but nonsense to listen to — a screen reader announces it
 * letter by letter. So the original text is restored as an aria-label and the
 * pieces are hidden from assistive technology.
 *
 * @param {HTMLElement} element The heading.
 * @returns {{split: SplitText, revert: () => void}}
 */
export function splitHeading(element) {
	const original = element.textContent;

	/*
	 * The words level is not decoration: with only lines and chars, every
	 * character is its own inline-block and the browser will happily wrap a
	 * heading mid-word ("Es-timate") the moment it runs short of room. Word
	 * spans keep the wrap points at word boundaries. Callers animate
	 * split.chars, same as before.
	 */
	const split = new SplitText(element, {
		type: 'lines,words,chars',
		linesClass: 'split-line',
	});

	element.setAttribute('aria-label', original);
	split.lines.forEach((line) => line.setAttribute('aria-hidden', 'true'));

	return {
		split,
		revert: () => {
			split.revert();
			element.removeAttribute('aria-label');
		},
	};
}

/**
 * Counts a number up as it scrolls into view.
 *
 * @param {HTMLElement} element  Where the number is written.
 * @param {number}      value    The number to land on.
 * @param {string}      [suffix] Appended to the number, for example '+'.
 * @returns {gsap.core.Tween}
 */
export function countUp(element, value, suffix = '') {
	const counter = { current: 0 };

	return gsap.to(counter, {
		current: value,
		duration: 1.8,
		ease: 'power2.out',
		scrollTrigger: { trigger: element, start: 'top 85%' },
		onUpdate: () => {
			element.textContent = Math.round(counter.current) + suffix;
		},
	});
}
